export const LISTING_IMPORT_KEYS = [
  'address',
  'city',
  'province',
  'postalCode',
  'askingPrice',
  'purchasePrice',
  'municipalEvaluation',
  'numberOfUnits',
  'structureType',
  'annualRent',
  'parkingRevenue',
  'internetRevenue',
  'storageRevenue',
  'otherRevenue',
  'vacancyRate',
  'municipalTaxes',
  'schoolTaxes',
  'insurance',
  'electricityHeating',
  'heating',
  'electricity',
  'maintenance',
  'concierge',
  'managementRate',
  'operatingExpenses',
  'otherExpenses',
  'landscaping',
  'snowRemoval',
  'extermination',
  'fireInspection',
  'advertising',
  'legal',
  'accounting',
  'cableInternet',
  'appliances',
  'garbage',
  'washerDryer',
  'hotWater',
  'numFridges',
  'numStoves',
  'numDishwashers',
  'numWashers',
  'numDryers',
  'numHeatPumps',
  'numElevators',
  'marketingDescriptionHtml',
];

const ADVANCED_EXPENSE_KEYS = [
  'heating',
  'electricity',
  'landscaping',
  'snowRemoval',
  'extermination',
  'fireInspection',
  'advertising',
  'legal',
  'accounting',
  'cableInternet',
  'appliances',
  'garbage',
  'washerDryer',
  'hotWater',
];

export const LISTING_PREVIEW_KEYS = [
  'address',
  'city',
  'province',
  'postalCode',
  'numberOfUnits',
  'askingPrice',
  'purchasePrice',
  'annualRent',
  'parkingRevenue',
  'vacancyRate',
  'municipalTaxes',
  'schoolTaxes',
  'insurance',
  'heating',
  'electricity',
  'electricityHeating',
  'operatingExpenses',
  'maintenance',
  'concierge',
  'managementRate',
];

/**
 * @param {Record<string, unknown>} fields
 * @returns {boolean}
 */
export function listingFieldsUseAdvancedExpenses(fields) {
  const f = fields || {};
  return ADVANCED_EXPENSE_KEYS.some((k) => f[k] != null && String(f[k]).trim() !== '');
}

/**
 * Rentalyzer stores maintenance and concierge as annual $ per dwelling.
 * Broker listings often give the building annual total.
 * @param {unknown} amount
 * @param {unknown} units
 * @returns {number | null}
 */
export function toPerUnitAnnualExpense(amount, units) {
  const n = Number(amount);
  const u = Math.round(Number(units));
  if (!Number.isFinite(n) || n <= 0) return Number.isFinite(n) ? n : null;
  if (!Number.isFinite(u) || u < 2) return n;
  if (n <= 1500) return n;
  const per = n / u;
  if (per >= 40 && per <= 4000) return Math.round(per * 100) / 100;
  return n;
}

/**
 * @param {Record<string, unknown>} fields
 * @returns {Record<string, unknown>}
 */
export function convertListingPerUnitExpenses(fields) {
  const next = { ...(fields || {}) };
  const units = Number(next.numberOfUnits);
  for (const key of ['maintenance', 'concierge']) {
    if (next[key] == null || next[key] === '') continue;
    const before = Number(next[key]);
    const after = toPerUnitAnnualExpense(next[key], units);
    if (after != null && Number.isFinite(before) && after !== before) {
      next[key] = String(after);
    }
  }
  return next;
}

/**
 * Merge extracted listing fields onto a property draft. Empty extracted values are skipped.
 * @param {Record<string, unknown>} baseProperty
 * @param {Record<string, unknown>} fields
 * @returns {Record<string, unknown>}
 */
export function applyListingImport(baseProperty, fields) {
  const next = { ...(baseProperty || {}) };
  const src = fields && typeof fields === 'object' ? fields : {};
  for (const key of LISTING_IMPORT_KEYS) {
    const v = src[key];
    if (v == null || v === '') continue;
    if (key === 'marketingDescriptionHtml') {
      const html = String(v).trim().slice(0, 20000);
      if (html) next.marketingDescriptionHtml = html;
      continue;
    }
    if (key === 'structureType' && v !== 'woodFrame' && v !== 'concrete') continue;
    next[key] = typeof v === 'string' || typeof v === 'number' ? v : String(v);
  }
  if (next.askingPrice && (next.purchasePrice === '' || next.purchasePrice == null)) {
    next.purchasePrice = next.askingPrice;
  }
  if (next.purchasePrice && (next.askingPrice === '' || next.askingPrice == null)) {
    next.askingPrice = next.purchasePrice;
  }
  return convertListingPerUnitExpenses(next);
}

/**
 * @param {Record<string, unknown>} fields
 * @returns {Array<{ key: string, value: string }>}
 */
export function listingPreviewRows(fields) {
  const f = fields || {};
  const rows = [];
  for (const key of LISTING_PREVIEW_KEYS) {
    const v = f[key];
    if (v == null || v === '') continue;
    rows.push({ key, value: String(v) });
  }
  return rows;
}
